import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { ProcessRunListItemType } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import { useGlobal } from 'context/GlobalContext';
import { useTranslation } from 'react-i18next';
import { getProcessRuns } from 'api/apiTask';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';

export const useProcessRuns = () => {
    const { t } = useTranslation(['common']);
    const { showToast } = useToast();
    const { g } = useGlobal();
    const location = useLocation();
    const onPage = useListOnPage();

    const [processes, setProcesses] = useState<ProcessRunListItemType[] | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [page, setPage] = useState<number | null>(null);
    const [listResetNonce, setListResetNonce] = useState(0);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);
    const prevListData = useRef<string | null>(null);
    const skipFetchOnStateChangeRef = useRef(false);

    const fetchProcesses = async (force = false) => {
        if (force) prevListData.current = null;
        const search = `%${g?.search?.[location.pathname] || ''}%`;
        if (!page || showSpinner) return null;
        const listData = { search, page: page < 0 ? 1 : page || 1, onPage };
        if (prevListData.current === JSON.stringify(listData)) return;
        prevListData.current = JSON.stringify(listData);
        setShowSpinner(true);
        const res = await getProcessRuns(listData);
        setShowSpinner(false);
        if (!res) return showToast(t('common:messError'), 'danger');
        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: res.paginator,
        }));
        setProcesses(res.processes);
    };

    const resetList = useCallback(async () => {
        skipFetchOnStateChangeRef.current = true;
        prevListData.current = null;
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);
        setShowSpinner(true);
        const res = await getProcessRuns({
            search: `%${g?.search?.[location.pathname] || ''}%`,
            page: 1,
            onPage,
        });
        setShowSpinner(false);
        if (!res) {
            skipFetchOnStateChangeRef.current = false;
            return showToast(t('common:messError'), 'danger');
        }
        prevListData.current = JSON.stringify({
            search: `%${g?.search?.[location.pathname] || ''}%`,
            page: 1,
            onPage,
        });
        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: res.paginator,
        }));
        setProcesses(res.processes);
        skipFetchOnStateChangeRef.current = false;
    }, [g, location.pathname, onPage, showToast, t]);

    useListRefreshEffect(listResetNonce, () => {
        void resetList();
    });
    useListRefreshEffect(listRefreshNonce, () => {
        prevListData.current = null;
        void fetchProcesses(true);
    });

    useEffect(() => {
        if (page === 1) void fetchProcesses(true);
        else setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [g.search?.[location.pathname], g.target]);

    useEffect(() => {
        if (skipFetchOnStateChangeRef.current) return;
        void fetchProcesses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, onPage]);

    const paginatorState = g?.paginator?.[location.pathname];
    const paginatorPage = paginatorState?.currentPage;
    const paginatorRows = paginatorState?.countRows;
    const paginatorOnPage = paginatorState?.onPage;

    useEffect(() => {
        const nextPage = paginatorPage || 1;
        if (page !== nextPage) setPage(nextPage);
    }, [paginatorPage, paginatorRows, paginatorOnPage, page]);

    return {
        processes,
        showSpinner,
        reload: () => {
            prevListData.current = null;
            void fetchProcesses(true);
        },
        triggerListReset: () => setListResetNonce((value) => value + 1),
        triggerListRefresh: () => setListRefreshNonce((value) => value + 1),
    };
};
